import React, { useEffect, useState } from 'react';

import { useProcurement } from '../context/ProcurementContext';

import { money } from './overviewHelpers';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useApiRequest from 'Hooks/useApiRequest';

import CloseIcon from 'Assets/icons/xmark.svg';

// A stage can hold thousands of procurements, so the inline list is paged: it fetches
// one small page at a time and appends via "Load more" rather than loading everything.
const PER_PAGE = 10;

/**
 * IB-699 — inline list of the procurements in a selected pipeline stage. Reuses the existing
 * procurement-plan items index endpoint, filtered by `procurement_status_id`, and pages it
 * with a "Load more" button so a large stage never renders (or fetches) thousands of rows.
 */
const StageProcurementsList = ({ stage, currencySymbol, onClose }) => {
    const { routes } = useProcurement();

    const { get: fetchItems, loading } = useApiRequest({ url: routes.fetchProcurementPlanItemsUrl });

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);

    // The index endpoint groups items by category; flatten to a single list for the stage view.
    const flatten = (payload) => {
        const groups = Array.isArray(payload?.data) ? payload.data : [];
        return groups.flatMap((group) => group.items ?? []);
    };

    const loadPage = async (nextPage, replace) => {
        if (!stage?.status_id) {
            return;
        }

        try {
            const response = await fetchItems({ procurement_status_id: stage.status_id, per_page: PER_PAGE, page: nextPage });
            const payload = response?.data ?? {};
            const fetched = flatten(payload);

            setItems((previous) => (replace ? fetched : [...previous, ...fetched]));
            setTotal(payload?.meta?.total ?? fetched.length);
            setPage(nextPage);
        } catch (error) {
            // Error toast is surfaced by useApiRequest.
        }
    };

    useEffect(() => {
        setItems([]);
        setTotal(0);
        setPage(0);
        loadPage(1, true);
    }, [stage?.status_id]);

    const hasMore = items.length < total;
    const isInitialLoading = loading && items.length === 0;

    return (
        <div className="tw-rounded-lg tw-border-2 tw-border-grey tw-p-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h5 className="tw-text-sm tw-font-semibold tw-text-charcoal">
                    {stage.status_name} <span className="tw-text-charcoal-50 tw-font-normal">({stage.count})</span>
                </h5>
                <button type="button" onClick={onClose} className="tw-text-charcoal-50 hover:tw-text-charcoal" aria-label={$t('Close')}>
                    <CloseIcon className="tw-w-4 tw-h-4" />
                </button>
            </div>

            {isInitialLoading ? (
                <LoadingSpinner />
            ) : items.length === 0 ? (
                <p className="tw-text-sm tw-text-charcoal-50">{$t('No procurements in this stage.')}</p>
            ) : (
                <>
                    <ul className="tw-divide-y tw-divide-grey">
                        {items.map((item) => (
                            <li key={item.id} className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2">
                                <div className="tw-min-w-0">
                                    <div className="tw-text-sm tw-font-medium tw-text-charcoal tw-truncate">{item.name}</div>
                                    <div className="tw-text-xs tw-text-charcoal-50 tw-truncate">
                                        {(item.departments_names ?? []).join(', ') || $t('No department')}
                                        {item.owner_name ? ` · ${item.owner_name}` : ''}
                                    </div>
                                </div>
                                <div className="tw-text-sm tw-font-semibold tw-text-charcoal tw-shrink-0">{money(currencySymbol, item.value)}</div>
                            </li>
                        ))}
                    </ul>

                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-3">
                        <span className="tw-text-xs tw-text-charcoal-50">
                            {items.length} / {total}
                        </span>
                        {hasMore && (
                            <button
                                type="button"
                                onClick={() => loadPage(page + 1, false)}
                                disabled={loading}
                                className="tw-text-sm tw-font-medium tw-text-blue hover:tw-underline disabled:tw-opacity-50"
                            >
                                {loading ? $t('Loading…') : $t('Load more')}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default StageProcurementsList;
